import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8MonthlyRoasPoint } from "../../p8OverviewData";
import { formatOverviewRoas, monthIndexFromLabel } from "../../p8OverviewData";
import type { AdsOrganizeBy, PrototypeVersion } from "../../types";
import { BREAKEVEN_ROAS_COLOR, useBreakevenRoas } from "../BreakevenRoas";
import { useCardPaddingClass } from "../CardDensity";
import { ForecastInlineLink, forecastTooltip } from "../ForecastExplainer";

import {
  ChartHeadline,
  ChartHoverPanel,
  ChartLegend,
  ChartLineLegendSwatch,
  ChartMonthAxisTick,
  ChartNavButtons,
  ChartTooltipExplore,
  CHART_LEGEND_GAP_CLASS,
  CHART_Y_AXIS_WIDTH,
  useChartPlotMargin,
  useChartPlotTopGapClass,
  useChartMonthWindow,
  useChartXAxisLabel,
  useChartHoverPanel,
  type ChartLegendItem,
  type ChartMonthWindowOptions,
} from "./chartShared";

const IMMEDIATE_COLOR = "#2d2a70";
const FIRST_YEAR_COLOR = "#AFC1FF";

const Y_AXIS_TICK_COUNT = 5;
const NICE_ROAS_STEPS = [
  0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12.5, 15, 20, 25, 50,
] as const;

type MonthlyRoasChartProps = {
  version?: PrototypeVersion;
  organizeBy: AdsOrganizeBy;
  data: P8MonthlyRoasPoint[];
  onOpenMonth?: (monthIndex: number) => void;
  /** Paging for ranges holding more months than the axis shows at once. */
  monthWindow?: ChartMonthWindowOptions;
};

const IMMEDIATE_LEGEND_ITEM: ChartLegendItem = {
  key: "immediateRoas",
  label: "Immediate ROAS",
  color: IMMEDIATE_COLOR,
  tooltip: COPY.overview.charts.roas.immediate,
};

// The only forecasted series in this chart, so it's the only legend item whose
// tooltip carries the explainer link.
const FIRST_YEAR_LEGEND_ITEM: ChartLegendItem = {
  key: "firstYearRoas",
  label: "First-year ROAS",
  color: FIRST_YEAR_COLOR,
  tooltip: forecastTooltip(COPY.overview.charts.roas.firstYear),
  interactiveTooltip: true,
};

const TOTAL_SALES_OVER_SPEND_ITEM: ChartLegendItem = {
  key: "totalSalesOverSpend",
  label: "Total sales / spend",
  color: FIRST_YEAR_COLOR,
  tooltip: COPY.overview.charts.roas.totalSalesOverSpend,
};

type SquareDotProps = { cx?: number; cy?: number; fill?: string };

function SquarePlotDot({ cx = 0, cy = 0, fill = "#333" }: SquareDotProps) {
  return (
    <rect
      x={cx - 3}
      y={cy - 3}
      width={6}
      height={6}
      rx={1}
      ry={1}
      fill={fill}
    />
  );
}

function computeYAxisLayout(
  data: P8MonthlyRoasPoint[],
  comparisonKey: "firstYearRoas" | "totalSalesOverSpend",
  referenceValue?: number
) {
  // First-year is the taller line when it's plotted; without it the axis should
  // close in on the immediate one rather than leaving its headroom empty.
  const dataMax = Math.max(
    0,
    referenceValue ?? 0,
    ...data.map((point) =>
      Math.max(
        point.immediateRoas,
        comparisonKey === "firstYearRoas"
          ? point.firstYearRoas
          : (point.totalSalesOverSpend ?? 0)
      )
    )
  );
  const minStep = Math.max(dataMax, 1) / (Y_AXIS_TICK_COUNT - 1);
  const step = NICE_ROAS_STEPS.find((candidate) => candidate >= minStep) ?? 100;
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

type ChartTooltipProps = {
  label: string;
  point: P8MonthlyRoasPoint;
  items: ChartLegendItem[];
  onExplore?: () => void;
};

function ChartTooltip({ label, point, items, onExplore }: ChartTooltipProps) {
  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">{label}</div>
      {items.map((item) => {
        const value =
          item.key === "immediateRoas"
            ? point.immediateRoas
            : item.key === "totalSalesOverSpend"
              ? (point.totalSalesOverSpend ?? 0)
              : point.firstYearRoas;
        return (
          <div key={item.key} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
            <span>{item.label}</span>
            <span className="ml-auto font-medium">
              {formatOverviewRoas(value)}
            </span>
          </div>
        );
      })}
      {onExplore ? <ChartTooltipExplore onClick={onExplore} /> : null}
    </div>
  );
}

type BreakevenLineShapeProps = {
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  onHover: (point: { x: number; y: number }) => void;
  onLeave: () => void;
};

/**
 * Dotted reference plus a wider invisible stroke so the pointer can find the
 * line. Recharts' own tooltip tracks months, not a y-value, so this reports
 * hover to the card and the card draws the ratio.
 */
function BreakevenLineShape({
  x1 = 0,
  y1 = 0,
  x2 = 0,
  y2 = 0,
  onHover,
  onLeave,
}: BreakevenLineShapeProps) {
  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={BREAKEVEN_ROAS_COLOR}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="0 6"
        pointerEvents="none"
      />
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="transparent"
        strokeWidth={12}
        pointerEvents="stroke"
        onMouseEnter={(event) => {
          event.stopPropagation();
          onHover({ x: event.clientX, y: event.clientY });
        }}
        onMouseMove={(event) => {
          event.stopPropagation();
          onHover({ x: event.clientX, y: event.clientY });
        }}
        onMouseLeave={onLeave}
      />
    </g>
  );
}

export function MonthlyRoasChart({
  version,
  organizeBy,
  data,
  onOpenMonth,
  monthWindow,
}: MonthlyRoasChartProps) {
  const isOrderMonth = organizeBy === "order-month";
  const isV2 = version != null && version >= 2;
  const isV3 = version === 3;
  const cardPaddingClass = useCardPaddingClass();
  const plotMargin = useChartPlotMargin();
  const plotTopGapClass = useChartPlotTopGapClass();
  const xAxisLabel = useChartXAxisLabel("Month");
  const breakevenRoas = useBreakevenRoas();
  const comparisonKey = isOrderMonth ? "totalSalesOverSpend" : "firstYearRoas";
  const {
    data: chartData,
    nav,
    slideRef,
  } = useChartMonthWindow(data, {
    pageable: monthWindow?.pageable ?? false,
    windowSize: monthWindow?.windowSize ?? data.length,
    resetKey: monthWindow?.resetKey ?? "",
  });
  // Scaled to every month the range holds, not just the visible window, so the
  // value axis stays put while paging moves the months underneath it.
  const { domain, ticks } = computeYAxisLayout(
    data,
    comparisonKey,
    breakevenRoas.enabled ? breakevenRoas.ratio : undefined
  );
  const comparisonItem = isOrderMonth
    ? {
        ...TOTAL_SALES_OVER_SPEND_ITEM,
        label: isV3 ? "Total ad sales / spend" : "Total sales / spend",
        tooltip: isV3
          ? COPY.overview.charts.roas.v3TotalSalesOverSpend
          : COPY.overview.charts.roas.totalSalesOverSpend,
      }
    : FIRST_YEAR_LEGEND_ITEM;
  const seriesLegendItems = [
    {
      ...IMMEDIATE_LEGEND_ITEM,
      tooltip: isV3
        ? COPY.overview.charts.roas.v3Immediate
        : COPY.overview.charts.roas.immediate,
    },
    comparisonItem,
  ];
  const legendItems = [
    ...seriesLegendItems,
    ...(breakevenRoas.enabled
      ? [
          {
            key: "breakevenRoas",
            label: "Breakeven ROAS",
            color: BREAKEVEN_ROAS_COLOR,
            tooltip: (
              <>
                {COPY.overview.charts.roas.breakeven}
                <button
                  type="button"
                  className="text-fs-text-primary-inverse mt-2 block text-left text-[14px] leading-[20px] underline"
                >
                  {COPY.overview.charts.roas.breakevenUpdate}
                </button>
              </>
            ),
            interactiveTooltip: true,
          } satisfies ChartLegendItem,
        ]
      : []),
  ];

  const {
    hover,
    boundsRef,
    onChartMouseMove,
    onChartMouseLeave,
    onPanelEnter,
    onPanelLeave,
  } = useChartHoverPanel();
  const hoveredPoint = hover
    ? chartData.find((point) => point.month === hover.label)
    : undefined;
  const [breakevenLineHover, setBreakevenLineHover] = useState<{
    x: number;
    y: number;
  } | null>(null);

  return (
    <div
      className={`bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] ${cardPaddingClass}`}
    >
      <ChartHeadline
        title={
          isOrderMonth
            ? isV3
              ? COPY.overview.charts.roas.v3OrderMonthTitle
              : COPY.overview.charts.roas.orderMonthTitle
            : COPY.overview.charts.roas.title
        }
        {...(isV2
          ? {
              description: isOrderMonth
                ? COPY.overview.charts.roas.orderMonthDescription
                : COPY.overview.charts.roas.description,
            }
          : {
              subtitle: isOrderMonth ? (
                COPY.overview.charts.roas.orderMonthDescription
              ) : (
                <>
                  {/* The link trails the sentence rather than sitting on its own
                      line: it answers the "forecasted" in the clause before it, and
                      a reader who isn't asking that question should be able to read
                      straight past it. */}
                  {COPY.overview.charts.roas.description} <ForecastInlineLink />
                </>
              ),
            })}
        actions={<ChartNavButtons nav={nav} />}
      />

      <div
        className={`${plotTopGapClass} flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        {/* Relative, so the hover panel can sit on the plot while remaining
            outside Recharts — required for an Explore button the pointer can
            actually reach. */}
        <div className="relative min-h-0 flex-1" ref={boundsRef}>
          <div className="h-full w-full overflow-hidden" ref={slideRef}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={plotMargin}
                onMouseMove={onChartMouseMove}
                onMouseLeave={onChartMouseLeave}
              >
                <CartesianGrid
                  stroke="#DFE0E1"
                  strokeDasharray="3 0"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  type="category"
                  scale="point"
                  padding={{ left: 16, right: 16 }}
                  tickLine={false}
                  axisLine={{ stroke: "#DFE0E1" }}
                  interval={0}
                  height={44}
                  tick={(props) => <ChartMonthAxisTick {...props} />}
                  label={xAxisLabel}
                />
                <YAxis
                  domain={domain}
                  ticks={ticks}
                  interval={0}
                  tick={{ fontSize: 11, fill: "#757575" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: number) => formatOverviewRoas(value)}
                  width={CHART_Y_AXIS_WIDTH}
                />
                {/* Kept only for the column highlight. The panel is rendered
                  below so it can hold a button. */}
                <Tooltip
                  cursor={{ stroke: "#dfe0e1", strokeWidth: 1 }}
                  content={() => null}
                />
                {breakevenRoas.enabled ? (
                  <ReferenceLine
                    y={breakevenRoas.ratio}
                    stroke={BREAKEVEN_ROAS_COLOR}
                    strokeWidth={1.5}
                    shape={(props) => (
                      <BreakevenLineShape
                        {...props}
                        onHover={(point) => {
                          const bounds =
                            boundsRef.current?.getBoundingClientRect();
                          if (!bounds) {
                            return;
                          }
                          setBreakevenLineHover({
                            x: point.x - bounds.left,
                            y: point.y - bounds.top,
                          });
                        }}
                        onLeave={() => setBreakevenLineHover(null)}
                      />
                    )}
                  />
                ) : null}
                <Line
                  type="linear"
                  dataKey="immediateRoas"
                  stroke={IMMEDIATE_COLOR}
                  strokeWidth={1.5}
                  dot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={IMMEDIATE_COLOR} />
                  )}
                  activeDot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={IMMEDIATE_COLOR} />
                  )}
                  isAnimationActive={false}
                />
                <Line
                  type="linear"
                  dataKey={comparisonKey}
                  stroke={FIRST_YEAR_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray={isOrderMonth ? undefined : "4 3"}
                  dot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={FIRST_YEAR_COLOR} />
                  )}
                  activeDot={(props: SquareDotProps) => (
                    <SquarePlotDot {...props} fill={FIRST_YEAR_COLOR} />
                  )}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {hover && hoveredPoint ? (
            <ChartHoverPanel
              hover={hover}
              onMouseEnter={onPanelEnter}
              onMouseLeave={onPanelLeave}
            >
              <ChartTooltip
                label={hover.label}
                point={hoveredPoint}
                items={seriesLegendItems}
                onExplore={
                  onOpenMonth
                    ? () => onOpenMonth(monthIndexFromLabel(hover.label))
                    : undefined
                }
              />
            </ChartHoverPanel>
          ) : null}

          {breakevenLineHover ? (
            <div
              className="bg-fs-surface-primary pointer-events-none absolute z-10 flex items-center gap-3 rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] whitespace-nowrap text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
              style={{
                left: breakevenLineHover.x + 12,
                top: Math.max(8, breakevenLineHover.y - 44),
              }}
            >
              <span>Breakeven ROAS</span>
              <span className="font-medium">
                {formatOverviewRoas(breakevenRoas.ratio)}
              </span>
            </div>
          ) : null}
        </div>

        <ChartLegend
          items={legendItems}
          renderSwatch={(item) =>
            item.key === "breakevenRoas" ? (
              <ChartLineLegendSwatch
                color={BREAKEVEN_ROAS_COLOR}
                dotted
                marker={false}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
